/**
 * Доменные типы агента ОСНОВА.Прораб (полевой сбор данных, AI-PRB-2026).
 *
 * Ввод данных ведётся прорабами в мобильном приложении; веб-витрина — обзор
 * площадок и собранных данных для офиса (ПТО/инженеры/админ). Зеркало DTO роутов
 * `/api/v1/agents/field/*`. snake_case 1:1 с backend. Предметные сущности
 * (рапорты, фото, заявки, аномалии) добавляются сюда по мере вывода на фронт.
 */

/** Статус модуля агента (витрина дашборда). */
export type FieldModuleStatus = 'planned' | 'active';

/** Один модуль агента ОСНОВА.Прораб. */
export interface FieldModule {
  code: string;
  title: string;
  status: FieldModuleStatus;
}

/** Карточка агента ОСНОВА.Прораб: описание + перечень модулей. */
export interface FieldInfo {
  agent_code: string;
  title: string;
  description: string;
  modules: FieldModule[];
}

/** Титул проекта. Документы назначает инженер явно, только внутри проекта. */
export interface FieldTitul {
  id: string;
  project_id: string;
  code: string;
  name: string;
  document_ids: string[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Площадка (зеркало DTO `/agents/field/sites`). snake_case 1:1 с backend. */
export interface FieldSite {
  id: string;
  project_id: string;
  /** null — прежняя площадка, ещё не распределена по титулам. */
  titul_id: string | null;
  name: string;
  code: string | null;
  location: string | null;
  timezone: string;
  /** active | archived. */
  status: string;
  /** Канал VK Teams площадки для дайджестов (L-7); null — не настроен. */
  vk_chat_id: string | null;
  /** Геозона площадки (МП-3.1): центр + радиус в метрах; все три null — не задана. */
  center_lat: number | null;
  center_lon: number | null;
  radius_m: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /** Проект ПТО площадки помечен на удаление (в корзине): площадка только для
   *  чтения — любая запись сервер отклоняет 409 до восстановления проекта.
   *  null — проект живой; поля нет — старый сервер. */
  project_deleted_at?: string | null;
}

/**
 * Площадка только для чтения: её проект ПТО в корзине. Кнопки записи (сверка,
 * приёмка, заявки, конструктив, задачи) не показываем — сервер их отклонит.
 */
export function siteReadOnly(
  site: Pick<FieldSite, 'project_deleted_at'> | null | undefined,
): boolean {
  return !!site?.project_deleted_at;
}

/** Человекочитаемая подпись статуса площадки (неизвестный → сам код). */
export function siteStatusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'активна';
    case 'archived':
      return 'в архиве';
    default:
      return status;
  }
}

/** Тон статус-метки площадки: активна — success, архив — нейтраль. */
export function siteStatusTone(status: string): 'success' | 'neutral' {
  return status === 'active' ? 'success' : 'neutral';
}

/** Дата ISO → `01.07.2026` (локаль ru). Пусто/невалид → прочерк/как есть. */
export function fmtFieldDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/* ── Роли: кто проверяет, кто ведёт площадку, кто подтверждает ──────────────── */

/**
 * Глобальные роли проверяющего ПТО — зеркало `GLOBAL_REVIEWER_ROLES` бэкенда
 * (`osnova-api/src/extractors.rs`, `reviewer_decision`): на всех площадках
 * сверяют и привязывают факты, принимают рапорты, ведут заявки и конструктив
 * из РД. «Прораб+» (`field_specialist_plus`) сюда не входит — он сам ведёт
 * площадку и свои объёмы не принимает; прораб (`foreman`) объём заявляет, а не
 * проверяет. Решает сервер; UI только не показывает кнопки, которые ответят 403.
 */
export const FIELD_REVIEWER_ROLES: readonly string[] = [
  'admin',
  'engineer',
  'engineer_plus',
  'manager',
];

/** Роли сквозного доступа ко всем площадкам (`has_global_field_access`): они
 *  заводят площадки и ведут их справочники (задачи, участники, конструктив). */
export const FIELD_GLOBAL_ROLES: readonly string[] = [
  ...FIELD_REVIEWER_ROLES,
  'field_specialist_plus',
];

/** Есть ли среди ролей пользователя глобальная роль проверяющего ПТО. */
export function isFieldReviewer(roles: readonly string[] | undefined): boolean {
  return (roles ?? []).some((r) => FIELD_REVIEWER_ROLES.includes(r));
}

/** Что пользователю можно в Прорабе по проекту (площадке) — для кнопок веба. */
export interface FieldAccess {
  /** Проверяющий ПТО: сверка и привязка фактов, приёмка/возврат рапортов,
   *  заявки и сборка АОСР, конструктив из РД, назначение прорабов, канал VK. */
  reviewer: boolean;
  /** Ведёт справочники площадки (задачи, участники): сквозной доступ или проверяющий. */
  manager: boolean;
  /** Сквозной доступ к Прорабу: только такие роли заводят новые площадки. */
  global: boolean;
}

/**
 * Права в Прорабе по проекту площадки (чистая логика, зеркало `site_access` /
 * `reviewer_decision` бэкенда). Проверяющий — глобальная роль проверяющего либо
 * участник проекта ПТО площадки (кроме наблюдателя `viewer`) с правом записи
 * агента `pto`. Все эти действия — запись в агенте Прораба: без права записи
 * `field` нельзя ничего.
 */
export function fieldAccessOf(input: {
  roles: readonly string[] | undefined;
  fieldCanWrite: boolean;
  ptoCanWrite: boolean;
  /** Роль в команде проекта ПТО площадки; null — не участник. */
  ptoMemberRole: string | null | undefined;
}): FieldAccess {
  if (!input.fieldCanWrite) return { reviewer: false, manager: false, global: false };
  const roles = input.roles ?? [];
  const global = roles.some((r) => FIELD_GLOBAL_ROLES.includes(r));
  const reviewer =
    isFieldReviewer(roles) ||
    (input.ptoCanWrite && !!input.ptoMemberRole && input.ptoMemberRole !== 'viewer');
  return { reviewer, manager: reviewer || global, global };
}

/** Участник площадки (зеркало DTO `/sites/{id}/members`). Имя и почту бэкенд
 *  может не прислать — тогда показываем роль без ФИО ([`foremenSummary`]). */
export interface FieldSiteMember {
  id: string;
  site_id: string;
  user_id: string;
  /** worker | foreman | site_chief. */
  role: string;
  created_at: string;
  full_name?: string | null;
  email?: string | null;
}

/** Подпись роли участника площадки. */
export function memberRoleLabel(role: string): string {
  switch (role) {
    case 'foreman':
      return 'прораб';
    case 'site_chief':
      return 'начальник участка';
    case 'worker':
      return 'рабочий';
    default:
      return role;
  }
}

/**
 * Прораб площадки: участник с ролью `foreman` | `site_chief` — только он
 * подтверждает правку объёма инженером (`confirm_volume_fact` бэкенда; за
 * прораба подтверждать нельзя, даже администратору).
 */
export function isSiteForeman(
  members: readonly FieldSiteMember[] | undefined,
  userId: string | undefined,
): boolean {
  if (!userId) return false;
  return (members ?? []).some(
    (m) => m.user_id === userId && (m.role === 'foreman' || m.role === 'site_chief'),
  );
}

/**
 * Кто ведёт площадку — строкой для карточки: ФИО с ролью, а если сервер имён не
 * прислал — сколько назначено по ролям («прораб — 2»). `null` — прораба нет:
 * рапорты по площадке сдавать некому.
 */
export function foremenSummary(members: readonly FieldSiteMember[] | undefined): string | null {
  const leads = (members ?? []).filter((m) => m.role === 'foreman' || m.role === 'site_chief');
  if (leads.length === 0) return null;
  const named = leads.filter((m) => m.full_name?.trim());
  if (named.length === leads.length) {
    return named.map((m) => `${m.full_name?.trim()} (${memberRoleLabel(m.role)})`).join(', ');
  }
  const byRole = new Map<string, number>();
  for (const m of leads) byRole.set(m.role, (byRole.get(m.role) ?? 0) + 1);
  return [...byRole]
    .map(([role, n]) => (n === 1 ? memberRoleLabel(role) : `${memberRoleLabel(role)} — ${n}`))
    .join(', ');
}

/** Кандидат в прорабы площадки (DTO `/sites/{id}/assignable-users`). */
export interface FieldAssignableUser {
  id: string;
  full_name: string;
  email: string;
  roles: string[];
}

/* ── Ввод объёма (П-4: число разбирает код, не модель) ─────────────────────── */

/**
 * Объём из поля ввода → положительное число или `null`. Принимает запятую как
 * десятичный разделитель и пробелы между разрядами («1 234,5»). Пустое, ноль,
 * минус, «1e3», «12 шт» — `null`: отправлять такое нельзя (раньше пустое поле
 * уходило нулём, а «−5» — отрицательным объёмом).
 */
export function parseQtyInput(raw: string): number | null {
  // `\s` в JS ловит и неразрывные пробелы (U+00A0, U+202F) — их приносит копирование.
  const s = raw.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const v = Number(s);
  return Number.isFinite(v) && v > 0 ? v : null;
}

/** Конструктивный элемент площадки (зеркало DTO `/sites/{id}/elements`). */
export interface FieldElement {
  id: string;
  project_id: string;
  site_id: string;
  /** Связь с позицией объёма ПТО — источник чисел АОСР (П1); null → проза в п.1. */
  position_id: string | null;
  code: string | null;
  title: string;
  axes: string | null;
  marks: string | null;
  section: string | null;
  titul: string | null;
  /** not_started | in_progress | ready_for_inspection | accepted | rework. */
  status: string;
  /** Кто ведёт статус (КR7): `manual` — выставлен проверяющим ПТО вручную,
   *  программа его не меняет; `auto` или null — статус считает программа по
   *  данным. Поля нет — старый сервер. */
  status_source?: string | null;
  needs_review: boolean;
  sort: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Заявка на освидетельствование (зеркало DTO `/sites/{id}/inspection-requests`). */
export interface FieldInspectionRequest {
  id: string;
  project_id: string;
  site_id: string;
  element_id: string;
  /** Наименование работ. */
  title: string;
  period_start: string | null;
  period_end: string | null;
  /** draft | sent | scheduled | passed | failed | act_generated. */
  status: string;
  /** FK на сгенерированный АОСР (мост field→pto); null — акт не собран. */
  executive_act_id: string | null;
  /** Акт заявки помечен на удаление или уже вычищен — АОСР можно пересобрать.
   *  Признак отдаёт сервер в списке заявок; нет поля (старый сервер) — веб
   *  уточняет состояние акта сам ([`inspectionActState`]). */
  act_deleted?: boolean | null;
  /** HITL-решение: accepted | rejected | disputed | null. */
  decision: string | null;
  decided_at: string | null;
  needs_review: boolean;
  note: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Погода, условия смены (`field_daily_reports.weather`, JSONB) — графа 3 ОЖР
 * «Условия производства работ»: строка, объект `{text|summary|conditions}` или
 * `{temp, wind_dir, wind_speed}`; `source` — кто внёс: `manual` (форма) или
 * `voice` (прораб назвал погоду в диктовке, L-2). `{}` — не указана.
 */
export type FieldReportWeather = string | Record<string, unknown> | null;

/** Сменный рапорт (зеркало DTO `/sites/{id}/daily-reports`; работы — отдельным GET). */
export interface FieldDailyReport {
  id: string;
  project_id: string;
  site_id: string;
  shift_date: string;
  /** day | night. */
  shift_type: string;
  author_id: string | null;
  /** ФИО автора из сервера, сохраняется при выходе сотрудника из команды площадки. */
  author_name?: string | null;
  /** Погода, условия смены ([`reportWeatherText`]); поля может не быть у старого сервера. */
  weather?: FieldReportWeather;
  /** draft | confirmed | accepted | returned. */
  status: string;
  return_comment: string | null;
  /** Проверяющий может вернуть ПРИНЯТЫЙ рапорт (КR4): есть отклонённые ПТО
   *  объёмы, решённые после последней сдачи рапорта. Только в карточке
   *  (`GET /daily-reports/{id}`); нет поля — старый сервер ([`canReturnAcceptedReport`]). */
  return_allowed?: boolean;
  /** Текст диктовки L-1 (Whisper); null — без голоса. */
  voice_transcript: string | null;
  /** §8.1 Этап 2b: прораб заверил зачитку распознанных чисел объёма через бота.
   *  Голосовой рапорт нельзя сдать (draft→confirmed), пока false. */
  voice_numbers_confirmed: boolean;
  /** Когда боту отправлена зачитка чисел; null — ещё не отправляли. */
  voice_readback_sent_at: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Строка работ рапорта (зеркало DTO `GET /daily-reports/{id}`; remainder —
 *  детерминированный расчёт план − факт на сервере, П-4). */
export interface FieldReportWork {
  id: string;
  report_id: string;
  element_id: string | null;
  work_item_code: string | null;
  name: string;
  unit: string | null;
  /** Объёмы — строками (П-4: числа не считает LLM; парсит и считает код). */
  qty_plan: string | null;
  qty_fact: string | null;
  comment: string | null;
  /** Уверенность L-2 (0..1); null — ручной ввод. */
  confidence: number | null;
  needs_review: boolean;
  source_ref: string | null;
  /** Источник строки: voice | llm | manual | null. */
  provenance: string | null;
  sort: number;
  /** Клиентский UUID строки (ПР-3, офлайн-идемпотентность); null — серверная. */
  client_uuid: string | null;
  /** Титул объекта из речи (§8.1 Этап 2, L-2); null — не назван. */
  titul_hint: string | null;
  /** Марка комплекта РД (АР/КЖ/ВК…) из речи. */
  marks_hint: string | null;
  /** Лист/шифр чертежа из речи. */
  drawing_hint: string | null;
  created_at: string;
  /** Остаток = план − факт (сервер, детерминированно); null — не посчитать. */
  remainder: number | null;
  /** Факт объёма строки и решение ПТО по нему (КR4); null — факта нет (строка
   *  без объёма, рапорт не сдан). Поля нет — старый сервер: факт ищем в
   *  фактах площадки по `work_id` ([`reportLineFact`]). */
  fact?: FieldReportLineFact | null;
}

/** Факт объёма строки рапорта в карточке рапорта (КR4): статус и объёмы решения
 *  ПТО. Числа — как отдаёт сервер (число или строка). */
export interface FieldReportLineFact {
  id: string;
  /** declared | corrected_pending_foreman | accepted | rejected | escalated. */
  status: string;
  qty: number | string | null;
  corrected_qty: number | string | null;
  /** Комментарий правки ПТО или причина возврата. */
  correction_note: string | null;
}

/** Ресурс смены: техника (kind=machine) или персонал (kind=personnel);
 *  числа — строками (П-4, считает сервер). Зеркало DTO `GET /daily-reports/{id}`. */
export interface FieldReportResource {
  id: string;
  report_id: string;
  /** machine | personnel. */
  kind: string;
  /** Тип техники («земснаряд») или специальность («машинист»). */
  name: string;
  /** Борт/гос.номер или ФИО. */
  ref: string | null;
  shifts: string | null;
  motohours: string | null;
  downtime_h: string | null;
  downtime_reason: string | null;
  /** Численность (personnel). */
  count: string | null;
  comment: string | null;
  confidence: number | null;
  needs_review: boolean;
  provenance: string | null;
  sort: number;
  client_uuid: string | null;
  created_at: string;
}

/** Расход материала за смену (зеркало DTO `GET /daily-reports/{id}`). */
export interface FieldReportUsage {
  id: string;
  report_id: string;
  name: string;
  qty: string | null;
  unit: string | null;
  comment: string | null;
  confidence: number | null;
  needs_review: boolean;
  provenance: string | null;
  sort: number;
  client_uuid: string | null;
  created_at: string;
}

/** Проблема/простой (issue) или заявка-потребность (request) смены. */
export interface FieldReportNote {
  id: string;
  report_id: string;
  /** issue | request. */
  kind: string;
  text: string;
  /** низкая | средняя | высокая (issue). */
  severity: string | null;
  confidence: number | null;
  needs_review: boolean;
  provenance: string | null;
  sort: number;
  client_uuid: string | null;
  created_at: string;
}

/** Задача/предписание прорабу (МП-7, зеркало DTO `/sites/{id}/tasks`). */
export interface FieldTask {
  id: string;
  site_id: string;
  /** pto | stroycontrol. */
  source: string;
  text: string;
  due_date: string | null;
  /** new | in_progress | done | declined. */
  status: string;
  decline_reason: string | null;
  response_comment: string | null;
  /** Требуется фотоответ (сценарий §4 ТЗ). */
  requires_photo: boolean;
  assignee_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Подпись статуса задачи (МП-7). */
export function taskStatusLabel(status: string): string {
  switch (status) {
    case 'new':
      return 'новая';
    case 'in_progress':
      return 'в работе';
    case 'done':
      return 'выполнена';
    case 'declined':
      return 'отклонена';
    default:
      return status;
  }
}

/** Тон статуса задачи. */
export function taskStatusTone(status: string): Tone {
  switch (status) {
    case 'done':
      return 'success';
    case 'declined':
      return 'danger';
    case 'in_progress':
      return 'info';
    default:
      return 'warning';
  }
}

/**
 * Мягкие флаги доказательной цепочки фото (`field_photos.integrity_flags`,
 * МП-3.1/ПР-10): приём не блокируют, но ПТО должен их видеть. Ставит сервер
 * (`osnova-storage/src/field/photos.rs`, `routes/field.rs::upload_photo`).
 */
export interface FieldPhotoIntegrityFlags {
  /** Снято вне геозоны площадки: расстояние от центра и радиус зоны, м. */
  geofence_outside?: { distance_m: number; radius_m: number };
  /** «Камерное» фото без EXIF — источник снимка не подтверждён. */
  exif_missing?: boolean;
  /** Время EXIF расходится с заявленным временем съёмки. */
  exif_time_mismatch?: boolean;
  /** Устройство сообщило о подменённых координатах (mock location). */
  mock_location?: boolean;
  /** Хеш предыдущего кадра серии «до/в процессе/после» — связка, не замечание. */
  prev_sha256?: string;
  [flag: string]: unknown;
}

/** Фото площадки (зеркало DTO `/sites/{id}/photos`; байты — отдельным
 *  запросом `/photos/{id}/data`). */
export interface FieldPhoto {
  id: string;
  project_id: string;
  site_id: string;
  report_id: string | null;
  element_id: string | null;
  inspection_request_id: string | null;
  /** Задача/предписание, на которую фото — фотоответ (МП-7). */
  task_id?: string | null;
  /** hidden_works | progress | incoming | defect | document | other. */
  category: string;
  work_item_code: string | null;
  series_id: string | null;
  series_order: number | null;
  lat: number | null;
  lon: number | null;
  accuracy: number | null;
  taken_at: string | null;
  server_received_at: string;
  sha256: string;
  source: string;
  mock_location_flag: boolean;
  /** Флаги достоверности для ПТО; `{}` — замечаний нет. */
  integrity_flags: FieldPhotoIntegrityFlags | null;
  content_type: string | null;
  author_id: string | null;
  /** ФИО автора из сервера, сохраняется при выходе сотрудника из команды площадки. */
  author_name?: string | null;
  created_at: string;
  deleted_at: string | null;
}

/** Метка достоверности фото для галереи. */
export interface PhotoIntegrityBadge {
  label: string;
  tone: Tone;
  /** Подсказка: что именно не так и почему это важно. */
  hint: string;
}

/**
 * Флаги достоверности фото → метки галереи, самые серьёзные первыми. Подмена
 * координат и съёмка вне геозоны ставят под сомнение, что фото с этой площадки;
 * без координат место съёмки не подтверждено вовсе (и геозону не проверить);
 * отсутствие/расхождение EXIF — что снимок сделан тогда и той камерой.
 */
export function photoIntegrityBadges(
  p: Pick<FieldPhoto, 'integrity_flags' | 'mock_location_flag'> &
    Partial<Pick<FieldPhoto, 'lat' | 'lon'>>,
): PhotoIntegrityBadge[] {
  const f: FieldPhotoIntegrityFlags = p.integrity_flags ?? {};
  const out: PhotoIntegrityBadge[] = [];
  if (f.mock_location === true || p.mock_location_flag) {
    out.push({
      label: 'подмена гео',
      tone: 'danger',
      hint:
        'Устройство сообщило о подменённых координатах (mock location) — место съёмки ' +
        'не подтверждено.',
    });
  }
  const geo = f.geofence_outside;
  if (geo && typeof geo === 'object') {
    const dist = Number(geo.distance_m);
    const radius = Number(geo.radius_m);
    const known = Number.isFinite(dist);
    out.push({
      label: known ? `вне геозоны · ${Math.round(dist)} м` : 'вне геозоны',
      tone: 'danger',
      hint: known
        ? `Снято в ${Math.round(dist)} м от центра площадки${
            Number.isFinite(radius) ? ` при радиусе геозоны ${Math.round(radius)} м` : ''
          }.`
        : 'Снято за пределами геозоны площадки.',
    });
  }
  // null — устройство координат не прислало; undefined — поле не пришло (не судим).
  if (p.lat === null || p.lon === null) {
    out.push({
      label: 'нет координат',
      tone: 'warning',
      hint:
        'Устройство не передало координаты съёмки — место не подтверждено, геозону не ' +
        'проверить.',
    });
  }
  if (f.exif_time_mismatch === true) {
    out.push({
      label: 'время EXIF ≠ съёмки',
      tone: 'warning',
      hint:
        'Время в EXIF камеры не сходится с заявленным временем кадра — снимок мог быть ' +
        'сделан в другой день или на другом устройстве.',
    });
  }
  if (f.exif_missing === true) {
    out.push({
      label: 'нет EXIF',
      tone: 'warning',
      hint:
        'В файле с камеры нет EXIF — модель устройства и исходное время съёмки ' +
        'не подтверждены.',
    });
  }
  return out;
}

/** Что не так с файлом фото, выяснилось при показе превью: сервер отдал не
 *  изображение (сигнатура файла не картинка) или браузер не смог его раскрыть. */
export type PhotoFileProblem = 'not_image' | 'broken';

/**
 * Метка галереи для файла, который не открывается как изображение. Такой кадр
 * всё равно уходит в «Приложения» АОСР и в фотоотчёт — ПТО должен его видеть.
 */
export function photoFileBadge(
  problem: PhotoFileProblem | null | undefined,
): PhotoIntegrityBadge | null {
  switch (problem) {
    case 'not_image':
      return {
        label: 'не изображение',
        tone: 'danger',
        hint:
          'Файл не является изображением (сигнатура не JPEG/PNG/…), хотя загружен как фото. ' +
          'В АОСР и фотоотчёте он бесполезен — попросите прораба переснять кадр.',
      };
    case 'broken':
      return {
        label: 'файл повреждён',
        tone: 'danger',
        hint:
          'Изображение не открывается: файл повреждён или обрезан. В АОСР и фотоотчёте он ' +
          'бесполезен — попросите прораба переснять кадр.',
      };
    default:
      return null;
  }
}

/** Подпись категории фото (неизвестная → сам код). */
export function photoCategoryLabel(category: string): string {
  switch (category) {
    case 'hidden_works':
      return 'скрытые работы';
    case 'progress':
      return 'ход работ';
    case 'incoming':
      return 'входной контроль';
    case 'defect':
      return 'дефект';
    case 'document':
      return 'документ';
    case 'other':
      return 'прочее';
    default:
      return category;
  }
}

/** Подпись источника строки рапорта (voice/llm/manual). */
export function workProvenanceLabel(provenance: string | null): string {
  switch (provenance) {
    case 'voice':
      return 'диктовка';
    case 'llm':
      return 'ИИ';
    case 'manual':
      return 'вручную';
    default:
      return '—';
  }
}

/** Аномалия площадки (зеркало DTO `/sites/{id}/anomalies`). */
export interface FieldAnomaly {
  owner_user_id?: string | null;
  personal_scope?: boolean;
  id: string;
  site_id: string;
  report_id: string | null;
  element_id: string | null;
  /** Тип аномалии (задаёт детектор L-6). */
  kind: string;
  /** critical | major | minor. */
  severity: string;
  /** Сообщение. */
  title: string;
  description: string | null;
  /** code | llm. */
  detected_by: string;
  needs_review: boolean;
  /** null — открыта, acknowledged — подтверждена (проверяющим) или принята к
   *  сведению (прорабом), rejected — ложная (только проверяющий ПТО, КR1). */
  decision: string | null;
  /** Кто и когда решил (КR1); полей нет — старый сервер, null — не записано
   *  (решение до учёта автора). */
  decided_by?: string | null;
  decided_by_name?: string | null;
  decided_at?: string | null;
  /** Решение принял проверяющий ПТО площадки: только оно окончательное — пересчёт
   *  находку не заводит заново. Решение прораба («принято к сведению») — нет. */
  decided_by_reviewer?: boolean;
  /** Версия LLM-фильтра (self-criticism), который перепроверил аномалию
   *  детектора; null — модель её не перепроверяла. */
  llm_filter_version: string | null;
  /** Данные находки. Фильтр шума L-6 пишет сюда `llm_suggests_reject` (модель
   *  считает находку шумом) и `llm_reject_reason` — решение всё равно за человеком. */
  payload?: Record<string, unknown> | null;
  created_at: string;
  deleted_at: string | null;
}

/** Что сказал о находке фильтр шума ИИ (L-6, шаг 2) — для того, кто решает. */
export interface AnomalyLlmView {
  /** Модель считает находку шумом (предлагает отклонить); причина — её словами. */
  noise: { reason: string | null } | null;
  /** Фильтр ИИ пересмотрел находку: понизил важность и/или пометил как шум. */
  reviewed: boolean;
}

/**
 * Вывод фильтра шума по аномалии. Модель решения не ставит: помечает «вероятно
 * шум» с причиной (`payload.llm_suggests_reject`) или меняет важность — закрыть
 * находку как ложную может только человек (П-4). Без пометки бейдж «перепроверено
 * ИИ» читался как «ИИ подтвердил», а предложение отклонить и его причина не
 * были видны вовсе.
 */
export function anomalyLlmView(
  a: Pick<FieldAnomaly, 'llm_filter_version'> & Partial<Pick<FieldAnomaly, 'payload'>>,
): AnomalyLlmView {
  const p = a.payload && typeof a.payload === 'object' ? a.payload : {};
  const reason = typeof p.llm_reject_reason === 'string' ? p.llm_reject_reason.trim() : '';
  return {
    noise: p.llm_suggests_reject === true ? { reason: reason || null } : null,
    reviewed: !!a.llm_filter_version,
  };
}

/**
 * Замечания разбора фото (vision) у аномалии — по одному на строку. Сервер
 * сводит замечания модели по одному кадру в одну аномалию (`payload.flags`,
 * КW4; `description` — те же замечания через «; »). Пустые и повторы (без учёта
 * регистра) отбрасываются. Массива нет (старая запись, другой вид аномалии) —
 * пусто: показываем `description`, как раньше.
 */
export function anomalyFlags(a: Partial<Pick<FieldAnomaly, 'payload'>>): string[] {
  const raw = a.payload && typeof a.payload === 'object' ? a.payload.flags : undefined;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of raw) {
    if (typeof f !== 'string') continue;
    const t = f.trim();
    const key = t.toLowerCase();
    if (!t || seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Подпись вида аномалии (детектор L-6 и разбор фото L-4; неизвестный → сам код). */
export function anomalyKindLabel(kind: string): string {
  switch (kind) {
    case 'qty_variance':
      return 'расхождение объёма';
    case 'confidence_low':
      return 'низкая уверенность распознавания';
    case 'photo_missing_hidden_work':
      return 'нет фото скрытых работ';
    case 'material_no_passport':
      return 'материал без паспорта';
    case 'visual_defect':
      return 'дефект на фото';
    case 'category_mismatch':
      return 'категория фото не совпадает';
    case 'volume_fact_escalated':
      return 'просрочено подтверждение объёма';
    default:
      return kind;
  }
}

/** Подпись важности аномалии (неизвестная → сам код). */
export function anomalySeverityLabel(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'критично';
    case 'major':
      return 'существенно';
    case 'minor':
      return 'незначительно';
    default:
      return severity;
  }
}

/** Подпись статуса элемента (неизвестный → сам код). */
export function elementStatusLabel(status: string): string {
  switch (status) {
    case 'not_started':
      return 'не начат';
    case 'in_progress':
      return 'в работе';
    case 'ready_for_inspection':
      return 'к освидетельствованию';
    case 'accepted':
      return 'принят';
    case 'rework':
      return 'на переделку';
    default:
      return status;
  }
}

/**
 * Как программа ведёт статус элемента (КR7): считает его из данных, а не «только
 * вперёд» — открытая заявка на освидетельствование (передана в ПТО или осмотр
 * назначен) — «к освидетельствованию»; иначе последняя заявка с замечаниями —
 * «на переделку»; иначе есть факты объёма — «в работе»; иначе «не начат».
 * «Принят» программа не ставит никогда: одна пройденная заявка покрывает часть
 * элемента (25 свай из 802), — элемент принимает проверяющий ПТО вручную.
 * Подсказка к метке статуса.
 */
export const ELEMENT_STATUS_AUTO_HINT =
  'Статус считает программа по данным элемента: открытая заявка на освидетельствование ' +
  '(передана в ПТО или осмотр назначен) — «к освидетельствованию»; последняя заявка с ' +
  'замечаниями — «на переделку»; есть факты объёма — «в работе»; иначе — «не начат». ' +
  '«Принят» программа не ставит: элемент целиком принимает проверяющий ПТО вручную.';

/** Подсказка к статусу, выставленному вручную (КR7). */
export const ELEMENT_STATUS_MANUAL_HINT =
  'Статус выставил проверяющий ПТО вручную — программа его не меняет, пока проверяющий ' +
  'не вернёт автоматический статус.';

/** Статус элемента выставлен вручную (КR7: `status_source = 'manual'`). */
export function elementStatusManual(el: Partial<Pick<FieldElement, 'status_source'>>): boolean {
  return el.status_source === 'manual';
}

/** Подсказка к метке статуса элемента: ручной или по данным (КR7). */
export function elementStatusHint(el: Partial<Pick<FieldElement, 'status_source'>>): string {
  return elementStatusManual(el) ? ELEMENT_STATUS_MANUAL_HINT : ELEMENT_STATUS_AUTO_HINT;
}

/** Ручное действие проверяющего со статусом элемента (`POST /elements/{id}/status`). */
export interface ElementStatusAction {
  /** `accepted` — отметить принятым (ручной статус); `auto` — вернуть статус программе. */
  status: 'accepted' | 'auto';
  label: string;
  title: string;
}

/**
 * Действия проверяющего ПТО со статусом элемента (КR7): «Отметить элемент
 * принятым» — пока элемент не принят вручную; «Вернуть автоматический статус» —
 * если статус ручной. Прорабу и без права проверяющего — ничего (сервер: 403).
 */
export function elementStatusActions(
  el: Pick<FieldElement, 'status'> & Partial<Pick<FieldElement, 'status_source'>>,
  reviewer: boolean,
): ElementStatusAction[] {
  if (!reviewer) return [];
  const manual = elementStatusManual(el);
  const out: ElementStatusAction[] = [];
  if (!(manual && el.status === 'accepted')) {
    out.push({
      status: 'accepted',
      label: 'Отметить элемент принятым',
      title:
        'Элемент принят целиком (все его работы освидетельствованы). Статус станет ручным — ' +
        'программа перестанет его менять.',
    });
  }
  if (manual) {
    out.push({
      status: 'auto',
      label: 'Вернуть автоматический статус',
      title: 'Статус снова посчитает программа по заявкам и фактам объёма элемента.',
    });
  }
  return out;
}

/** Тон статуса элемента. */
export function elementStatusTone(status: string): Tone {
  switch (status) {
    case 'accepted':
      return 'success';
    case 'ready_for_inspection':
      return 'warning';
    case 'in_progress':
      return 'info';
    case 'rework':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Подпись статуса заявки на освидетельствование (неизвестный → сам код). */
export function requestStatusLabel(status: string): string {
  switch (status) {
    case 'draft':
      return 'черновик';
    case 'sent':
      return 'отправлена';
    case 'scheduled':
      return 'назначена';
    case 'passed':
      return 'освидетельствовано';
    case 'failed':
      return 'замечания';
    case 'act_generated':
      return 'акт собран';
    default:
      return status;
  }
}

/** Тон статуса заявки. */
export function requestStatusTone(status: string): Tone {
  switch (status) {
    case 'passed':
    case 'act_generated':
      return 'success';
    case 'failed':
      return 'danger';
    case 'scheduled':
    case 'sent':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Подпись статуса сменного рапорта (неизвестный → сам код). */
export function reportStatusLabel(status: string): string {
  switch (status) {
    case 'draft':
      return 'черновик';
    case 'confirmed':
      return 'подтверждён';
    case 'accepted':
      return 'принят';
    case 'returned':
      return 'возвращён';
    default:
      return status;
  }
}

/** Тон статуса рапорта. */
export function reportStatusTone(status: string): Tone {
  switch (status) {
    case 'accepted':
      return 'success';
    case 'returned':
      return 'danger';
    case 'confirmed':
      return 'info';
    default:
      return 'neutral';
  }
}

/* ── Погода смены: графа 3 ОЖР «Условия производства работ» (КW1) ──────────── */

/** Сколько символов погоды принимает сервер (после обрезки пробелов). */
export const REPORT_WEATHER_MAX = 200;

/** Скаляр JSON → непустая строка (зеркало `scalar` в `osnova-pto/src/ozhr.rs`). */
function weatherScalar(v: unknown): string | null {
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : null;
  if (typeof v === 'boolean') return String(v);
  return null;
}

/**
 * Погода смены строкой — так её печатает графа 3 ОЖР (зеркало `weather_str` в
 * `osnova-pto/src/ozhr.rs`): строка — как есть; объект — `text`, `summary` или
 * `conditions`, иначе сборка «{temp}° {wind_dir} {wind_speed} м/с». Пусто (`{}`,
 * пробелы, null, поля нет) — null: погода не указана.
 */
export function reportWeatherText(w: unknown): string | null {
  if (typeof w === 'string') return w.trim() || null;
  if (!w || typeof w !== 'object' || Array.isArray(w)) return null;
  const o = w as Record<string, unknown>;
  for (const key of ['text', 'summary', 'conditions']) {
    const v = o[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  const parts: string[] = [];
  const temp = weatherScalar(o.temp);
  if (temp) parts.push(`${temp}°`);
  const dir = weatherScalar(o.wind_dir);
  if (dir) parts.push(dir);
  const speed = weatherScalar(o.wind_speed);
  if (speed) parts.push(`${speed} м/с`);
  return parts.length > 0 ? parts.join(' ') : null;
}

/** Погоду назвал прораб в диктовке — её записал разбор L-2 (`source: 'voice'`).
 *  В карточке — пометка «из диктовки»: модель могла расслышать не так. */
export function reportWeatherFromVoice(w: unknown): boolean {
  if (!w || typeof w !== 'object' || Array.isArray(w)) return false;
  return (w as Record<string, unknown>).source === 'voice' && reportWeatherText(w) !== null;
}

/**
 * Кто правит погоду смены (КR8): проверяющий ПТО площадки — в любом статусе
 * рапорта (графу 3 ОЖР сданного и принятого рапорта иначе не заполнить никому;
 * сервер переносит погоду и в строки ОЖР); писатель площадки (прораб) — пока
 * рапорт у него, в черновике или после возврата, как строки рапорта (иначе 409).
 */
export function canEditReportWeather(
  status: string,
  siteWriter: boolean,
  reviewer = false,
): boolean {
  if (reviewer) return true;
  return siteWriter && (status === 'draft' || status === 'returned');
}

/** Что не так с введённой погодой; null — можно сохранять (пустое — очистить). */
export function reportWeatherInputError(raw: string): string | null {
  const len = [...raw.trim()].length;
  return len > REPORT_WEATHER_MAX
    ? `Не длиннее ${REPORT_WEATHER_MAX} символов (сейчас ${len}): коротко, как в журнале.`
    : null;
}

/** Дата `ГГГГ-ММ-ДД…` → `ДД.ММ.ГГГГ` (как в строках ОЖР), без часовых поясов;
 *  не дата — null. */
export function isoDateRu(value: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}

/** Строки ОЖР без «Условий» (графа 3): сколько их и за какие даты (по порядку, без повторов). */
export interface OzhrMissingWeather {
  count: number;
  dates: string[];
}

/** Строки ОЖР с пустой графой 3 «Условия производства работ» (погода смены). */
export function ozhrMissingWeather(
  rows: readonly { date: string | null; conditions: string | null }[],
): OzhrMissingWeather {
  const dates: string[] = [];
  let count = 0;
  for (const r of rows) {
    if (r.conditions?.trim()) continue;
    count += 1;
    const d = r.date?.trim();
    if (d && !dates.includes(d)) dates.push(d);
  }
  return { count, dates };
}

/**
 * Рапорты смены без погоды за даты строк ОЖР с пустой графой 3 — куда идти
 * указывать погоду (КR8: проверяющий — в любом статусе рапорта). Даты — как в
 * ОЖР (`ДД.ММ.ГГГГ`); сравнение с датой смены без часовых поясов.
 */
export function reportsMissingWeather<
  R extends Pick<FieldDailyReport, 'shift_date'> & Partial<Pick<FieldDailyReport, 'weather'>>,
>(reports: readonly R[], dates: readonly string[]): R[] {
  return reports.filter((r) => {
    const d = isoDateRu(r.shift_date);
    return !!d && dates.includes(d) && reportWeatherText(r.weather) === null;
  });
}

/** Тон важности аномалии: critical — danger, major — warning, minor — нейтраль. */
export function anomalySeverityTone(severity: string): Tone {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'major':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Подпись решения по аномалии (HITL): null — открыта. */
export function anomalyDecisionLabel(decision: string | null): string {
  switch (decision) {
    case 'acknowledged':
      return 'принята';
    case 'rejected':
      return 'отклонена';
    default:
      return 'открыта';
  }
}

/** Решение по аномалии для карточки: метка, тон, кто и когда, окончательно ли. */
export interface AnomalyDecisionView {
  label: string;
  tone: Tone;
  /** Пояснение к метке (что значит решение и чьё оно). */
  hint: string;
  /** «Решение ПТО: Иванов И.И., 10.10.2026» и т. п.; null — решения нет или автор не записан. */
  by: string | null;
  /** Решение окончательное (проверяющий ПТО или старый сервер): кнопок больше нет. */
  final: boolean;
}

/**
 * Решение по аномалии словами (КR1). Окончательное — только решение проверяющего
 * ПТО: «подтверждена» или «ложная» (её пересчёт уже не заведёт). Прораб площадки
 * только «принимает к сведению» — находку о его же работе он не закрывает: она
 * ждёт решения ПТО, а пересчёт может завести её снова. Решение без отметки о
 * проверяющем при новом сервере (`decided_by_reviewer: false`) — принятое до
 * учёта автора: ПТО может его пересмотреть. Старый сервер (полей нет) — как
 * раньше: «принята» / «отклонена», окончательно.
 */
export function anomalyDecisionView(
  a: Pick<FieldAnomaly, 'decision'> &
    Partial<Pick<FieldAnomaly, 'decided_by_name' | 'decided_at' | 'decided_by_reviewer'>>,
): AnomalyDecisionView {
  if (!a.decision) {
    return {
      label: 'открыта',
      tone: 'warning',
      hint: 'Решения по находке нет: верна она или ложная, решает проверяющий ПТО.',
      by: null,
      final: false,
    };
  }
  const who = a.decided_by_name?.trim() || null;
  const when = a.decided_at ? fmtFieldDate(a.decided_at) : null;
  const tail = [who, when].filter(Boolean).join(', ');
  if (a.decided_by_reviewer === undefined) {
    return {
      label: anomalyDecisionLabel(a.decision),
      tone: 'neutral',
      hint: 'Решение по находке принято.',
      by: tail ? `Решение: ${tail}` : null,
      final: true,
    };
  }
  if (a.decided_by_reviewer) {
    const rejected = a.decision === 'rejected';
    return {
      label: rejected ? 'ложная — решение ПТО' : 'подтверждена ПТО',
      tone: 'neutral',
      hint: rejected
        ? 'Проверяющий ПТО признал находку ложной — пересчёт её не заведёт снова.'
        : 'Проверяющий ПТО подтвердил находку.',
      by: tail ? `Решение ПТО: ${tail}` : 'Решение ПТО',
      final: true,
    };
  }
  if (a.decision === 'acknowledged') {
    return {
      label: 'принята к сведению',
      tone: 'warning',
      hint:
        'Прораб принял находку к сведению — это не решение ПТО: верна она или ложная, решает ' +
        'проверяющий ПТО, а пересчёт может завести её снова.',
      by: tail ? `Принял к сведению: ${tail}` : null,
      final: false,
    };
  }
  return {
    label: anomalyDecisionLabel(a.decision),
    tone: 'neutral',
    hint:
      'Решение принято до того, как стали записывать его автора, — проверяющий ПТО может ' +
      'его пересмотреть.',
    by: tail ? `Решение: ${tail}` : null,
    final: false,
  };
}

/** Кнопка решения по аномалии. */
export interface AnomalyDecisionAction {
  decision: 'acknowledged' | 'rejected';
  label: string;
  title: string;
}

/**
 * Какие решения по аномалии показать (КR1): проверяющему ПТО — «Подтвердить» и
 * «Ложная», пока окончательного решения ПТО нет (в том числе поверх «принято к
 * сведению» прорабом); писателю площадки без права проверяющего (прорабу) —
 * только «Принять к сведению» и только по открытой находке: закрыть её как
 * ложную он не может (сервер: 403). Остальным — ничего.
 */
export function anomalyDecisionActions(
  a: Pick<FieldAnomaly, 'decision'> & Partial<Pick<FieldAnomaly, 'decided_by_reviewer'>>,
  who: { reviewer: boolean; siteWriter: boolean },
): AnomalyDecisionAction[] {
  if (who.reviewer) {
    if (anomalyDecisionView(a).final) return [];
    return [
      {
        decision: 'acknowledged',
        label: 'Подтвердить',
        title: 'Находка верна — решение ПТО.',
      },
      {
        decision: 'rejected',
        label: 'Ложная',
        title: 'Находка ложная — решение ПТО: пересчёт её больше не заведёт.',
      },
    ];
  }
  if (who.siteWriter && !a.decision) {
    return [
      {
        decision: 'acknowledged',
        label: 'Принять к сведению',
        title:
          'Вы видели находку. Верна она или ложная, решает проверяющий ПТО; пересчёт может ' +
          'завести её снова.',
      },
    ];
  }
  return [];
}

/** Срез сверки факта объёма (заявлено + ранее принятое ↔ РД). Зеркало
 *  `osnova_pto::FactReconcile` + пометки `reconcile_fact` бэкенда; при
 *  непарсируемом объёме — {severity:'unparsed'}. Новые поля старый сервер не
 *  присылает — все необязательные. */
export interface FactReconcile {
  /** ok | minor | major | critical | no_rd | unparsed. */
  severity: string;
  /** within_rd | over_executed | no_rd_position | unit_mismatch | rd_no_qty | unparsed. */
  kind?: string;
  rd_qty?: number | null;
  /** Ранее принято по позиции — только тот же вид работ и сопоставимая единица. */
  prior_qty?: number;
  declared_qty?: number;
  total_qty?: number;
  delta?: number | null;
  delta_pct?: number | null;
  message?: string;
  /** Сколько принятых фактов вошло в «Ранее принято». */
  prior_count?: number;
  /** На чём основано «Ранее принято»: `same_work_kind_and_unit`. */
  prior_basis?: string;
  /** Ещё не решённые заявки того же вида работ на позиции: объём и число. */
  pending_qty?: number;
  pending_count?: number;
  /** Факты других работ или в другой единице на той же позиции — не суммируются. */
  other_kind_count?: number;
  /** Вместе с нерешёнными заявками того же вида объём выходит за РД. */
  pending_over_rd?: boolean;
  /** Пометка разбора объёма кодом («1,5 тысячи» → 1500, хвост не единица). */
  qty_note?: string;
  /** Числа из диктовки прораб через бота не заверял (нет VK ID, перезапись). */
  numbers_unconfirmed?: boolean;
  /** `auto` — элемент конструктива строки подобрал матчер, а не человек:
   *  привязку нужно подтвердить (её снимает привязка ПТО, POST /bind). */
  element_match?: string;
}

/** Факт выполненного объёма (§8.1, зеркало DTO `/volume-facts`). Числа — строкой
 *  (qty_raw, П-4) + распознанное qty; принятое = corrected_qty ?? qty. */
export interface VolumeFact {
  id: string;
  project_id: string;
  site_id: string;
  report_id: string;
  work_id: string;
  element_id: string | null;
  /** Позиция объёма РД (эталон сверки); null → сверить не с чем. */
  position_id: string | null;
  work_item_code: string | null;
  name: string;
  unit: string | null;
  titul: string | null;
  /** Марка комплекта РД (АР/КЖ/ВК…); наполняет Этап 2 (голосовой ввод). */
  mark: string | null;
  drawing: string | null;
  /** Объём «как в рапорте» (П-4). */
  qty_raw: string;
  /** Распознанный объём; null — не распознан (needs_review). */
  qty: number | null;
  /** Правка инженера ПТО (ждёт подтверждения прораба); null — не правился. */
  corrected_qty: number | null;
  correction_note: string | null;
  reconcile: FactReconcile;
  /** declared | corrected_pending_foreman | accepted | rejected | escalated. */
  status: string;
  needs_review: boolean;
  foreman_deadline: string | null;
  verified_by: string | null;
  verified_at: string | null;
  accepted_by: string | null;
  accepted_at: string | null;
  source_ref: string | null;
  created_at: string;
  updated_at: string;
}

/** Событие журнала переходов факта (§8.1, зеркало DTO `/volume-facts/{id}`). */
export interface VolumeFactEvent {
  id: number;
  fact_id: string;
  /** declared | accepted | corrected | rejected | foreman_confirmed | escalated |
   *  bound (смена элемента/позиции РД; статус факта не меняется). У `escalated`
   *  в `payload.reason` причина: `foreman_disputed` — прораб не согласен с
   *  правкой ПТО (`comment`, КW2), `sla_no_foreman_confirm` — правку не
   *  подтвердили в срок (`deadline`, система). */
  event: string;
  from_status: string | null;
  to_status: string;
  actor_id: string | null;
  /** foreman | engineer | system. */
  actor_role: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

/** Кто совершил событие журнала факта (роль из события). */
export function factEventActorLabel(role: string | null): string {
  switch (role) {
    case 'foreman':
      return 'прораб';
    case 'engineer':
      return 'ПТО';
    case 'system':
      return 'система';
    default:
      return '—';
  }
}

/** Способ перепривязки факта в событии `bound`: элементу назначили позицию РД,
 *  привязки перенесли на новую ревизию РД или переизвлечение объёмов ревизии
 *  перенесло ссылку на двойника позиции (`relink`, автоматически); без `via` —
 *  привязка факта к элементу конструктива. */
function boundVia(payload: Record<string, unknown> | null | undefined): string | null {
  const via = payload?.via;
  return via === 'element' || via === 'carry_over' || via === 'relink' ? via : null;
}

/** Причина эскалации факта (событие `escalated`): прораб не согласен с правкой
 *  ПТО (КW2) или правку не подтвердили в срок (SLA; старые события — без причины). */
export type FactEscalationReason = 'foreman_disputed' | 'overdue';

/** Причина эскалации из `payload` события `escalated`. */
export function factEscalationReason(
  payload: Record<string, unknown> | null | undefined,
): FactEscalationReason {
  return payload?.reason === 'foreman_disputed' ? 'foreman_disputed' : 'overdue';
}

/** Почему факт сейчас на эскалации — из последнего события `escalated` журнала. */
export interface FactEscalation {
  reason: FactEscalationReason;
  /** С чем не согласен прораб (его комментарий); null — нет или это просрочка. */
  comment: string | null;
  /** Срок подтверждения правки, который истёк (просрочка); null — нет. */
  deadline: string | null;
  /** Когда факт ушёл на эскалацию (прораб ответил «не согласен» или истёк срок). */
  at: string;
  actorId: string | null;
}

/**
 * Эскалация факта сейчас: статус `escalated` и последнее событие `escalated`
 * журнала — несогласие прораба с правкой ПТО (`reason: 'foreman_disputed'`,
 * КW2) или просрочка подтверждения (SLA). И то и другое ждёт решения ПТО
 * (принять, поправить или вернуть); по просрочке прораб ещё может и
 * подтвердить. Не на эскалации — null. События — в порядке журнала (сервер
 * отдаёт по времени).
 */
export function activeFactEscalation(
  status: string,
  events: readonly Pick<VolumeFactEvent, 'event' | 'payload' | 'created_at' | 'actor_id'>[],
): FactEscalation | null {
  if (status !== 'escalated') return null;
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const ev = events[i];
    if (ev.event !== 'escalated') continue;
    const p: Record<string, unknown> = ev.payload ?? {};
    const reason = factEscalationReason(p);
    return {
      reason,
      comment: reason === 'foreman_disputed' ? str(p.comment) : null,
      deadline: reason === 'overdue' ? str(p.deadline) : null,
      at: ev.created_at,
      actorId: ev.actor_id,
    };
  }
  return null;
}

/** Подпись события журнала факта (неизвестное → сам код). */
export function factEventLabel(ev: Pick<VolumeFactEvent, 'event' | 'payload'>): string {
  switch (ev.event) {
    case 'declared':
      return 'заявлено';
    case 'accepted':
      return 'принято';
    case 'corrected':
      return 'правка ПТО';
    case 'rejected':
      return 'возврат';
    case 'foreman_confirmed':
      return 'подтверждено прорабом';
    case 'escalated':
      return factEscalationReason(ev.payload) === 'foreman_disputed'
        ? 'прораб не согласен'
        : 'просрочено (эскалация)';
    case 'bound':
      return boundVia(ev.payload) ? 'привязка к позиции РД' : 'привязка к конструктиву';
    default:
      return ev.event;
  }
}

/**
 * Подробности события журнала факта из его `payload` (числа — как записал
 * сервер, П-4): заявленный объём, принятый объём, правка «было → стало» с
 * комментарием, причина возврата, несогласие прораба с правкой (его
 * комментарий) или срок просроченной правки, перепривязка к конструктиву
 * «откуда → куда».
 * Событие `bound` (смена элемента/позиции РД факта): `from_element_id` /
 * `to_element_id`, `from_position_id` / `to_position_id`; элементу назначили
 * позицию — `via: 'element'`, привязки перенесли на новую ревизию РД —
 * `via: 'carry_over'`, переизвлечение объёмов перенесло или сняло ссылку —
 * `via: 'relink'`; тот же элемент без `via` — ПТО подтвердил привязку,
 * подобранную программой. `elementTitle` — подпись элемента площадки по id
 * (null — элемента нет в конструктиве). Нечего сказать — пустая строка.
 */
export function factEventDetail(
  ev: Pick<VolumeFactEvent, 'event' | 'payload'>,
  elementTitle: (id: string) => string | null,
): string {
  const p: Record<string, unknown> = ev.payload ?? {};
  const num = (v: unknown): string | null =>
    typeof v === 'number' && Number.isFinite(v) ? String(Math.round(v * 1000) / 1000) : null;
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
  switch (ev.event) {
    case 'declared': {
      const qty = str(p.qty_raw) ?? num(p.qty);
      const unit = str(p.unit);
      return qty ? `${qty}${unit ? ` ${unit}` : ''}` : '';
    }
    case 'accepted': {
      const qty = num(p.qty);
      return qty ? `принято ${qty}` : '';
    }
    case 'corrected': {
      const after = num(p.qty_after);
      const change = after !== null ? `${num(p.qty_before) ?? '—'} → ${after}` : null;
      return [change, str(p.note)].filter(Boolean).join(' · ');
    }
    case 'rejected': {
      const note = str(p.note);
      return note ? `причина: ${note}` : '';
    }
    case 'escalated': {
      // Прораб не согласен — его комментарий (решение за ПТО); просрочка — срок.
      if (factEscalationReason(p) === 'foreman_disputed') {
        const comment = str(p.comment);
        return comment ? `«${comment}»` : 'без комментария';
      }
      const deadline = str(p.deadline);
      return deadline ? `правку не подтвердили до ${fmtFieldDate(deadline)}` : '';
    }
    case 'bound': {
      const via = boundVia(p);
      const fromPos = str(p.from_position_id);
      const toPos = str(p.to_position_id);
      if (via === 'carry_over') {
        return toPos
          ? 'перенесён на позицию действующей ревизии РД'
          : 'перенос на новую ревизию РД';
      }
      if (via === 'relink') {
        // Переизвлечение объёмов ревизии: ссылка ушла на двойника позиции или
        // снята (двойника нет — привязать заново вручную).
        return toPos
          ? 'повторное извлечение объёмов РД: привязка перенесена на ту же позицию'
          : 'повторное извлечение объёмов РД: позиция не найдена — привязка снята, привяжите заново';
      }
      if (via === 'element') {
        if (!toPos) return 'у элемента снята позиция РД';
        return fromPos && fromPos !== toPos
          ? 'у элемента сменилась позиция РД'
          : 'элементу назначена позиция РД';
      }
      const title = (v: unknown): string | null => {
        const id = str(v);
        return id ? (elementTitle(id) ?? 'элемент вне конструктива') : null;
      };
      const to = title(p.to_element_id);
      const parts: string[] = [];
      if (to && str(p.from_element_id) === str(p.to_element_id)) {
        // Тот же элемент: ПТО подтвердил привязку, подобранную программой.
        parts.push(`привязка подтверждена: ${to}`);
      } else if (to) {
        parts.push(`${title(p.from_element_id) ?? 'без элемента'} → ${to}`);
      }
      if ('to_position_id' in p && !toPos) parts.push('у элемента нет позиции РД');
      return parts.join(' · ');
    }
    default:
      return '';
  }
}

/** Принятый объём факта: правка инженера, если была, иначе заявленное. */
export function factAcceptedQty(f: Pick<VolumeFact, 'qty' | 'corrected_qty'>): number | null {
  return f.corrected_qty ?? f.qty;
}

/** Строка «принятый объём» карточки факта: подпись, число (null — прочерк) и
 *  пояснение, почему числа нет или почему оно ещё не окончательное. */
export interface FactAcceptedView {
  label: string;
  value: number | null;
  note: string | null;
}

/**
 * «Принятый объём» — только у принятого факта; правка ПТО, ждущая прораба, —
 * объём «к принятию», а не принятый. На сверке и после возврата принятого объёма
 * нет: раньше карточка показывала «Принятый объём 12» у возвращённого факта.
 */
export function factAcceptedView(
  f: Pick<VolumeFact, 'status' | 'qty' | 'corrected_qty'>,
): FactAcceptedView {
  switch (f.status) {
    case 'accepted':
      return { label: 'Принятый объём', value: factAcceptedQty(f), note: null };
    case 'corrected_pending_foreman':
      return {
        label: 'К принятию (правка ПТО)',
        value: factAcceptedQty(f),
        note: 'станет принятым после подтверждения прораба',
      };
    case 'escalated':
      // Эскалация (прораб не согласен или просрочил): решает и ПТО (КW2).
      return {
        label: 'К принятию (правка ПТО)',
        value: factAcceptedQty(f),
        note: 'станет принятым после подтверждения прораба или решения ПТО',
      };
    case 'rejected':
      return { label: 'Принятый объём', value: null, note: 'не принят — объём возвращён прорабу' };
    default:
      return {
        label: 'Принятый объём',
        value: null,
        note: 'ещё не принят — объём на сверке у ПТО',
      };
  }
}

/** Подпись статуса факта объёма. */
export function volumeFactStatusLabel(status: string): string {
  switch (status) {
    case 'declared':
      return 'на сверке';
    case 'corrected_pending_foreman':
      return 'правка — ждёт прораба';
    case 'accepted':
      return 'принят';
    case 'rejected':
      return 'возвращён';
    case 'escalated':
      // Две причины — прораб не согласен с правкой (КW2) или не подтвердил её в
      // срок; какая — видно в карточке факта (журнал). Решает и ПТО.
      return 'эскалация';
    default:
      return status;
  }
}

/** Тон статуса факта: принят — success, эскалация/возврат — danger. */
export function volumeFactStatusTone(status: string): Tone {
  switch (status) {
    case 'accepted':
      return 'success';
    case 'escalated':
    case 'rejected':
      return 'danger';
    case 'corrected_pending_foreman':
      return 'warning';
    default:
      return 'info';
  }
}

/** Тон severity сверки факта: critical/no_rd/unparsed — danger, major —
 *  warning, minor — info, ok — success. */
export function factReconcileTone(severity: string): Tone {
  switch (severity) {
    case 'critical':
    case 'no_rd':
    case 'unparsed':
      return 'danger';
    case 'major':
      return 'warning';
    case 'minor':
      return 'info';
    case 'ok':
      return 'success';
    default:
      return 'neutral';
  }
}

/** Подпись severity сверки факта. */
export function factReconcileLabel(severity: string): string {
  switch (severity) {
    case 'ok':
      return 'в пределах РД';
    case 'minor':
      return 'небольшое отклонение';
    case 'major':
      return 'превышение РД';
    case 'critical':
      return 'критическое превышение';
    case 'no_rd':
      return 'нет позиции РД';
    case 'unparsed':
      return 'объём не распознан';
    default:
      return severity;
  }
}

/** Как читать сверку факта: метка, тон и пояснение, что с этим делать. */
export interface FactReconcileView {
  label: string;
  tone: Tone;
  /** Что делать инженеру; null — пояснять нечего (сообщение сервера достаточно). */
  hint: string | null;
}

/**
 * Сверка факта для человека. Вид (`kind`) точнее, чем важность: «ед. изм. не
 * совпадает» и «в РД нет количества» по важности могут прийти как `no_rd`, но
 * лечатся по-разному — первое правкой единицы или другой позицией, второе только
 * сверкой по чертежу вручную. Неизвестный вид → подпись по важности.
 * `ctx.elementBound` — факт уже привязан к элементу конструктива: «нет позиции
 * РД» тогда значит, что позиции нет у самого элемента, и совет «привяжите к
 * конструктиву» был бы советом сделать уже сделанное. Что сделать, говорит
 * текст сервера («у элемента нет позиции РД — привяжите элемент к позиции на
 * странице площадки»), подсказка только показывает, где это место.
 * `ctx.reviewer === false` — смотрит не проверяющий (прораб): привязку к
 * конструктиву и позиции РД и сверку по чертежу делает ПТО (прорабу сервер
 * ответит 403), поэтому подсказка не командует, а объясняет, кто это сделает.
 */
export function factReconcileView(
  rec: FactReconcile | null | undefined,
  ctx?: { elementBound?: boolean; reviewer?: boolean },
): FactReconcileView {
  if (!rec) return { label: 'сверка не выполнялась', tone: 'neutral', hint: null };
  const foreman = ctx?.reviewer === false;
  if (rec.kind === 'no_rd_position' && ctx?.elementBound) {
    return {
      label: 'у элемента нет позиции РД',
      tone: 'danger',
      hint: foreman
        ? 'У элемента нет позиции РД — сверить объём не с чем. Позицию РД элементу назначает ' +
          'инженер ПТО.'
        : 'На странице площадки: раздел «Конструктив» → у этого элемента «Привязать к позиции ' +
          'РД…». Сверка его фактов пересчитается.',
    };
  }
  switch (rec.kind) {
    case 'unit_mismatch':
      return {
        label: 'единица не совпадает с РД',
        tone: 'warning',
        hint: foreman
          ? 'Объём заявлен в другой единице, чем позиция РД, — сравнить числа нельзя. Проверьте ' +
            'единицу в рапорте; привязку к позиции РД делает инженер ПТО.'
          : 'Объём заявлен в другой единице, чем позиция РД, — сравнить числа нельзя. Проверьте ' +
            'единицу в рапорте или привяжите факт к позиции РД в той же единице.',
      };
    case 'rd_no_qty':
      return {
        label: 'в РД нет количества',
        tone: 'warning',
        hint: foreman
          ? 'У позиции РД не указано количество — сверять не с чем: объём сверит инженер ПТО ' +
            'по чертежу.'
          : 'У позиции РД не указано количество — сверять не с чем. Сверьте объём по чертежу ' +
            'вручную и примите или поправьте факт.',
      };
    case 'no_rd_position':
      return {
        label: 'нет позиции РД',
        tone: 'danger',
        hint: foreman
          ? 'Факт не привязан к объёму РД — сверить не с чем. Привязку к конструктиву и позиции ' +
            'РД делает инженер ПТО.'
          : 'Факт не привязан к объёму РД. Привяжите его к конструктиву площадки — у элемента ' +
            'с позицией РД сверка пересчитается.',
      };
    default:
      return {
        label: factReconcileLabel(rec.severity),
        tone: factReconcileTone(rec.severity),
        hint: null,
      };
  }
}

/** Пометка сверки факта — короткой меткой (реестры, карточка) и подсказкой. */
export interface FactReconcileFlag {
  label: string;
  tone: Tone;
  hint: string;
}

/**
 * Пометки сверки, требующие внимания ПТО до решения по факту: привязку к
 * элементу подобрала программа (её нужно подтвердить), числа из диктовки не
 * заверены прорабом, вместе с нерешёнными заявками того же вида — сверх РД,
 * объём пересчитан кодом («1,5 тысячи» → 1500). Только у нерешённого факта: у
 * принятого и возвращённого решение уже принято, а пометки остаются в тексте
 * сверки сервера. Автопривязку предлагаем подтвердить, пока привязку можно
 * менять ([`canBindFact`]). `opts.reviewer === false` — смотрит прораб:
 * привязку проверяет ПТО, метка без «проверьте».
 */
export function factReconcileFlags(
  rec: FactReconcile | null | undefined,
  status: string,
  opts?: { reviewer?: boolean },
): FactReconcileFlag[] {
  if (!rec || !isFactPending(status)) return [];
  const out: FactReconcileFlag[] = [];
  if (rec.element_match === 'auto' && canBindFact(status)) {
    // Метка короткая (узкие карточки реестра), смысл — в подсказке.
    out.push(
      opts?.reviewer === false
        ? {
            label: 'автопривязка',
            tone: 'warning',
            hint:
              'Элемент конструктива подобрала программа по наименованию работы — привязку ' +
              'проверит инженер ПТО.',
          }
        : {
            label: 'автопривязка — проверьте',
            tone: 'warning',
            hint:
              'Привязка подобрана автоматически: элемент конструктива выбрала программа по ' +
              'наименованию работы, а не человек. Подтвердите привязку или перепривяжите факт в ' +
              'его карточке.',
          },
    );
  }
  if (rec.numbers_unconfirmed === true) {
    out.push({
      label: 'числа не заверены',
      tone: 'warning',
      hint: 'Числа из диктовки прораб через бота не заверял — сверьте объём с рапортом.',
    });
  }
  if (rec.pending_over_rd === true) {
    out.push({
      label: 'с нерешёнными — сверх РД',
      tone: 'warning',
      hint:
        'По отдельности в пределах РД, но вместе с ещё не решёнными заявками того же вида ' +
        'работ объём выходит за РД.',
    });
  }
  const note = rec.qty_note?.trim();
  if (note) {
    out.push({ label: 'объём пересчитан', tone: 'warning', hint: `Разбор объёма: ${note}.` });
  }
  return out;
}

/** Строка разбивки сверки в карточке факта. */
export interface FactReconcileRow {
  label: string;
  value: string;
}

/** Число сверки → строка до тысячных (П-4: считает сервер, здесь только вывод). */
function fmtReconcileQty(v: number | null | undefined): string {
  return typeof v === 'number' && Number.isFinite(v) ? String(Math.round(v * 1000) / 1000) : '—';
}

/**
 * Разбивка сверки факта с РД: из чего сложилось «Итого». «Ранее принято» сервер
 * считает только по тому же виду работ и в сопоставимой единице — подпись это
 * называет (у старого сервера основы нет — подпись прежняя), рядом число
 * вошедших фактов. Ещё не решённые заявки того же вида — отдельной строкой: в
 * итог они не входят, но вместе с ними объём может выйти за РД. Факты других
 * работ на позиции — «не суммируются», чтобы инженер не искал их в итоге. Без
 * объёма РД разбивки нет.
 */
export function factReconcileRows(rec: FactReconcile | null | undefined): FactReconcileRow[] {
  if (!rec || rec.rd_qty === null || rec.rd_qty === undefined) return [];
  const rows: FactReconcileRow[] = [{ label: 'Объём по РД', value: fmtReconcileQty(rec.rd_qty) }];
  const sameKind = rec.prior_basis === 'same_work_kind_and_unit';
  rows.push({
    label: sameKind ? 'Ранее принято (этот вид работ)' : 'Ранее принято',
    value:
      typeof rec.prior_count === 'number'
        ? `${fmtReconcileQty(rec.prior_qty ?? 0)} — фактов: ${rec.prior_count}`
        : fmtReconcileQty(rec.prior_qty),
  });
  if (typeof rec.pending_qty === 'number' && rec.pending_qty > 0) {
    rows.push({
      label: 'Ещё на сверке (этот вид работ)',
      value: `${fmtReconcileQty(rec.pending_qty)}${
        typeof rec.pending_count === 'number' ? ` — заявок: ${rec.pending_count}` : ''
      }${rec.pending_over_rd ? ' (вместе — сверх РД)' : ''}`,
    });
  }
  rows.push({ label: 'Итого с учётом факта', value: fmtReconcileQty(rec.total_qty) });
  if (typeof rec.delta_pct === 'number' && Number.isFinite(rec.delta_pct)) {
    rows.push({
      label: 'Отклонение',
      value: `${rec.delta_pct > 0 ? '+' : ''}${rec.delta_pct}%`,
    });
  }
  if (typeof rec.other_kind_count === 'number' && rec.other_kind_count > 0) {
    rows.push({
      label: 'Не суммируются',
      value: `фактов других работ или в другой единице на позиции: ${rec.other_kind_count}`,
    });
  }
  return rows;
}

/* ── Автоматы статусов: какие кнопки показывать (решает сервер, UI — зеркало) ── */

/** Сверку (принять / поправить / вернуть) проверяющий ведёт по заявленному факту
 *  и по эскалации — прораб не согласен с правкой ПТО или не подтвердил её в срок:
 *  решение за ПТО (КW2, тот же `verify`). */
export function canVerifyFact(status: string): boolean {
  return status === 'declared' || status === 'escalated';
}

/** «Принять как есть» — только когда объём распознан: принимать нечего, если
 *  числа нет (сервер ответит 400 — тут только «Поправить объём»). */
export function canAcceptFact(f: Pick<VolumeFact, 'status' | 'qty' | 'corrected_qty'>): boolean {
  return canVerifyFact(f.status) && (f.corrected_qty ?? f.qty) !== null;
}

/** Привязка факта к конструктиву — пока факт не принят и не возвращён. */
export function canBindFact(status: string): boolean {
  return status === 'declared' || status === 'corrected_pending_foreman';
}

/** Правку ПТО подтверждает прораб, пока она ждёт его (в том числе просроченная). */
export function canConfirmFact(status: string): boolean {
  return status === 'corrected_pending_foreman' || status === 'escalated';
}

/** «Не согласен» с правкой ПТО — прораб площадки, пока правка ждёт его ответа
 *  (КW2: только из `corrected_pending_foreman`; после ответа факт на эскалации). */
export function canDisputeFact(status: string): boolean {
  return status === 'corrected_pending_foreman';
}

/** Комментарий несогласия: обязателен, от 3 до 1000 символов (как на сервере). */
export const DISPUTE_COMMENT_MIN = 3;
export const DISPUTE_COMMENT_MAX = 1000;

/** Что не так с комментарием несогласия; null — можно отправлять. */
export function disputeCommentError(raw: string): string | null {
  const len = [...raw.trim()].length;
  if (len < DISPUTE_COMMENT_MIN) {
    return `Напишите ПТО, с чем вы не согласны (от ${DISPUTE_COMMENT_MIN} символов).`;
  }
  if (len > DISPUTE_COMMENT_MAX) {
    return `Не длиннее ${DISPUTE_COMMENT_MAX} символов (сейчас ${len}).`;
  }
  return null;
}

/**
 * Элемент факта подобрала программа (`reconcile.element_match = 'auto'`), и
 * привязку ещё можно менять: ПТО подтверждает её привязкой к тому же элементу
 * (POST /bind снимает пометку) или перепривязывает к другому.
 */
export function canConfirmFactBinding(
  f: Pick<VolumeFact, 'status' | 'element_id' | 'reconcile'>,
): boolean {
  return !!f.element_id && f.reconcile?.element_match === 'auto' && canBindFact(f.status);
}

/**
 * «Принять» / «Вернуть» рапорт — только из `confirmed` (прораб сдал) и только
 * проверяющему ПТО. Черновик ещё у прораба; принятый и возвращённый уже решены —
 * повторно их не трогаем (сервер ответит 409).
 */
export function canReviewReport(status: string, reviewer: boolean): boolean {
  return reviewer && status === 'confirmed';
}

/** Факт объёма ещё не решён: на сверке у ПТО или правка ПТО ждёт прораба
 *  (в том числе просроченная). */
export function isFactPending(status: string): boolean {
  return status === 'declared' || status === 'corrected_pending_foreman' || status === 'escalated';
}

/** Факты рапорта для приёмки: сколько не решено и сколько возвращено прорабу. */
export interface ReportFactsSummary {
  pending: number;
  rejected: number;
}

/** Сводка фактов рапорта (П-4: считает код) — для приёмки и возврата рапорта. */
export function reportFactsSummary(
  facts: readonly Pick<VolumeFact, 'status'>[],
): ReportFactsSummary {
  let pending = 0;
  let rejected = 0;
  for (const f of facts) {
    if (isFactPending(f.status)) pending += 1;
    else if (f.status === 'rejected') rejected += 1;
  }
  return { pending, rejected };
}

/**
 * «Принять рапорт» — сданный рапорт, все факты объёма которого решены. Принятый
 * рапорт закрывает строки: факт, который ПТО вернёт уже после приёмки, прораб
 * не переподаст, а второй рапорт за ту же смену не сдать. Сервер в этом случае
 * отвечает 409 с числом нерешённых фактов — веб не даёт до него дойти.
 */
export function canAcceptReport(status: string, reviewer: boolean, pendingFacts: number): boolean {
  return canReviewReport(status, reviewer) && pendingFacts === 0;
}

/**
 * Вернуть ПРИНЯТЫЙ рапорт на исправление — проверяющему и только за отклонения,
 * которые прораб ещё не отрабатывал (КR4): сервер считает `return_allowed` —
 * есть отклонённые ПТО объёмы, решённые после последней сдачи рапорта. Раньше
 * хватало любого отклонения, и после пересдачи с новой строкой и повторной
 * приёмки веб снова требовал «верните рапорт» — бесконечный цикл: отклонённую
 * строку прораб не исправит и не удалит (она заблокирована решением ПТО).
 * `returnAllowed` не пришёл (старый сервер) — прежнее правило: есть возвращённые.
 */
export function canReturnAcceptedReport(
  status: string,
  reviewer: boolean,
  rejectedFacts: number,
  returnAllowed?: boolean | null,
): boolean {
  if (!reviewer || status !== 'accepted') return false;
  return typeof returnAllowed === 'boolean' ? returnAllowed : rejectedFacts > 0;
}

/**
 * Пометка «нужна проверка» у факта — только пока по нему нет решения ПТО: у
 * принятого и возвращённого решение уже принято, флаг извлечения больше ничего
 * не значит (раньше «нужна проверка» висела и на решённых фактах).
 */
export function factNeedsReview(f: Pick<VolumeFact, 'status' | 'needs_review'>): boolean {
  return f.needs_review && isFactPending(f.status);
}

/** Число из JSON (число или строка с точкой/запятой); иное — null. */
function jsonNum(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v.trim().replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Объём до тысячных, как в карточках («8», «12.5»). */
function fmtQty(v: number): string {
  return String(Math.round(v * 1000) / 1000);
}

/** Объём факта в реестре площадки: главное число и пояснение к нему. */
export interface FactQtyView {
  /** «Принято: 8 шт» или «Заявлено: 148 шт». */
  primary: string;
  /** «заявлено 148 шт», «правка ПТО: 8 шт», «не принят»; null — пояснять нечего. */
  secondary: string | null;
}

/**
 * Объём факта для карточки в реестре (B9): у принятого — принятый объём (правка
 * ПТО, если была) и рядом «заявлено N», если отличается; у правки, ждущей
 * прораба, и эскалации — заявленное и правка ПТО; у возвращённого — «не
 * принят». Раньше у принятого с правкой было «ПРИНЯТ · Заявлено: 148 шт» — и
 * читалось «принято 148» вместо принятых 8.
 */
export function factQtyView(
  f: Pick<VolumeFact, 'status' | 'qty' | 'corrected_qty' | 'qty_raw' | 'unit'>,
): FactQtyView {
  const unit = f.unit?.trim() ? ` ${f.unit.trim()}` : '';
  const declared = `${f.qty_raw}${unit}`;
  const corrected = f.corrected_qty;
  switch (f.status) {
    case 'accepted': {
      const accepted = factAcceptedQty(f);
      if (accepted === null) return { primary: `Принято: ${declared}`, secondary: null };
      const changed = corrected !== null && (f.qty === null || Math.abs(corrected - f.qty) > 1e-9);
      return {
        primary: `Принято: ${fmtQty(accepted)}${unit}`,
        secondary: changed ? `заявлено ${declared}` : null,
      };
    }
    case 'corrected_pending_foreman':
    case 'escalated':
      return {
        primary: `Заявлено: ${declared}`,
        secondary: corrected !== null ? `правка ПТО: ${fmtQty(corrected)}${unit}` : null,
      };
    case 'rejected':
      return { primary: `Заявлено: ${declared}`, secondary: 'не принят' };
    default:
      return { primary: `Заявлено: ${declared}`, secondary: null };
  }
}

/**
 * Факт строки рапорта: из ответа карточки (`work.fact`, КR4), а у старого
 * сервера — из фактов площадки по `work_id`. Поле `fact` пришло (в том числе
 * null — факта нет) — верим серверу.
 */
export function reportLineFact(
  work: Pick<FieldReportWork, 'id'> & Partial<Pick<FieldReportWork, 'fact'>>,
  facts: readonly Pick<
    VolumeFact,
    'id' | 'work_id' | 'status' | 'qty' | 'corrected_qty' | 'correction_note'
  >[],
): FieldReportLineFact | null {
  if (work.fact !== undefined) return work.fact;
  const f = facts.find((x) => x.work_id === work.id);
  return f
    ? {
        id: f.id,
        status: f.status,
        qty: f.qty,
        corrected_qty: f.corrected_qty,
        correction_note: f.correction_note,
      }
    : null;
}

/** Решение ПТО по строке рапорта — для колонки «Решение ПТО» карточки рапорта. */
export interface ReportLineDecision {
  label: string;
  tone: Tone;
  /** «принято 8 шт (заявлено 10)», «правка ПТО: 8 шт», «причина: …»; null — нечего. */
  detail: string | null;
  /** По строке есть решение ПТО: исправить или удалить её нельзя (КR4, сервер — 409). */
  locked: boolean;
}

/**
 * Решение ПТО по факту строки рапорта (КR4): статус факта словами и числа
 * решения. Строка с фактом не на сверке «заблокирована решением ПТО» — ни
 * исправить, ни удалить её прораб не может; так видно, какую строку отклонили
 * и почему. Факта нет — null.
 */
export function reportLineDecision(
  fact: FieldReportLineFact | null | undefined,
  unit: string | null | undefined,
): ReportLineDecision | null {
  if (!fact) return null;
  const u = unit?.trim() ? ` ${unit.trim()}` : '';
  const qty = jsonNum(fact.qty);
  const corrected = jsonNum(fact.corrected_qty);
  const note = fact.correction_note?.trim() || null;
  const base = {
    label: volumeFactStatusLabel(fact.status),
    tone: volumeFactStatusTone(fact.status),
    locked: fact.status !== 'declared',
  };
  switch (fact.status) {
    case 'accepted': {
      const accepted = corrected ?? qty;
      const changed = corrected !== null && qty !== null && Math.abs(corrected - qty) > 1e-9;
      return {
        ...base,
        detail:
          accepted === null
            ? null
            : `принято ${fmtQty(accepted)}${u}${changed && qty !== null ? ` (заявлено ${fmtQty(qty)})` : ''}`,
      };
    }
    case 'corrected_pending_foreman':
    case 'escalated':
      return {
        ...base,
        detail:
          [corrected !== null ? `правка ПТО: ${fmtQty(corrected)}${u}` : null, note]
            .filter(Boolean)
            .join(' — ') || null,
      };
    case 'rejected':
      return { ...base, detail: note ? `причина: ${note}` : null };
    default:
      return { ...base, detail: null };
  }
}

/** Рапорт сдал сам пользователь (автор) — КR11: свой рапорт и его объёмы он не
 *  принимает и не сверяет как проверяющий ПТО (сервер — 403). */
export function isOwnReport(
  authorId: string | null | undefined,
  userId: string | null | undefined,
): boolean {
  return !!authorId && !!userId && authorId === userId;
}

/** Может ли прораб площадки подтвердить правку ПТО сейчас; если нет — почему. */
export interface ForemanConfirmView {
  allowed: boolean;
  /** Почему кнопки «Подтвердить объём» нет; null — объяснять нечего. */
  reason: string | null;
}

/**
 * «Подтвердить объём» прорабом (КR11): правка ждёт его (`corrected_pending_foreman`)
 * или просрочена (эскалация по сроку). После «Не согласен» решение за ПТО —
 * подтверждать нельзя (сервер — 409 «вы не согласились с правкой»); правку,
 * которую он сам внёс как проверяющий ПТО, — тоже (сервер — 403).
 */
export function foremanConfirmView(
  f: Pick<VolumeFact, 'status' | 'verified_by'>,
  escalation: Pick<FactEscalation, 'reason'> | null,
  userId: string | null | undefined,
): ForemanConfirmView {
  if (!canConfirmFact(f.status)) return { allowed: false, reason: null };
  if (f.status === 'escalated' && escalation?.reason === 'foreman_disputed') {
    return {
      allowed: false,
      reason: 'Вы не согласились с правкой — решение по объёму за ПТО.',
    };
  }
  if (userId && f.verified_by === userId) {
    return {
      allowed: false,
      reason:
        'Правку внесли вы сами как проверяющий ПТО — подтвердить её как прораб нельзя: ' +
        'подтверждает другой прораб площадки, или решение примет другой проверяющий ПТО.',
    };
  }
  return { allowed: true, reason: null };
}

/** Код причины, по которой зачитка чисел не ушла (`reason` в теле ответа
 *  `POST /daily-reports/{id}/voice-readback` с 409/422); null — не пришёл. */
export function voiceReadbackReason(err: unknown): string | null {
  if (typeof err !== 'object' || err === null || !('data' in err)) return null;
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;
  const reason = (data as { reason?: unknown }).reason;
  return typeof reason === 'string' && reason ? reason : null;
}

/**
 * Текст веба для причины, где важно, ЧТО делать дальше: VK ID задаёт
 * администратор (раздел «Пользователи»), а не прораб «в профиле»; без VK ID
 * рапорт сдаётся без заверения, а числа из диктовки уходят ПТО на проверку.
 * null — достаточно текста сервера.
 */
export function voiceReadbackSkipText(reason: string | null | undefined): string | null {
  switch (reason) {
    case 'no_vk_user_id':
      return (
        'У автора рапорта не задан VK ID — зачитку некуда отправить. VK ID (VK Teams userId) ' +
        'задаёт администратор в разделе «Пользователи». Без заверения рапорт сдаётся, а числа ' +
        'из диктовки уходят ПТО на проверку.'
      );
    default:
      return null;
  }
}

/** Переход заявки на освидетельствование, доступный ПТО в текущем статусе. */
export interface InspectionTransition {
  /** Целевой статус (`POST /inspection-requests/{id}/status`). */
  status: string;
  label: string;
  variant: 'primary' | 'secondary' | 'danger' | 'ghost';
  /** Пояснение к кнопке (что изменится). */
  title?: string;
}

/**
 * Действия ПТО по заявке (зеркало матрицы переходов бэкенда): прораб передаёт
 * заявку (draft → sent), ПТО назначает освидетельствование (sent → scheduled) и
 * фиксирует итог (scheduled → passed | failed). Назначить можно и по черновику:
 * мобильное приложение заводит заявку черновиком, шага «передать в ПТО» в нём
 * нет. После замечаний — повторное освидетельствование (failed → scheduled).
 * Итог «пройдено» можно исправить на «есть замечания», пока акт не собран
 * (passed → failed): это и путь к повторному освидетельствованию, если по
 * пройденной заявке поставлено отрицательное решение. Сборка АОСР — отдельная
 * кнопка ([`canGenerateAct`]).
 */
export function inspectionTransitions(status: string): InspectionTransition[] {
  switch (status) {
    case 'draft':
    case 'sent':
      return [{ status: 'scheduled', label: 'Назначить освидетельствование', variant: 'primary' }];
    case 'scheduled':
      return [
        { status: 'passed', label: 'Освидетельствовано', variant: 'primary' },
        // Не danger: рядом с primary две красные кнопки неразличимы (бренд красный).
        { status: 'failed', label: 'Есть замечания', variant: 'secondary' },
      ];
    case 'passed':
      return [
        {
          status: 'failed',
          label: 'Исправить: есть замечания',
          variant: 'ghost',
          title:
            'Итог освидетельствования — «есть замечания»: после устранения назначьте ' +
            'освидетельствование повторно. Пока акт не собран.',
        },
      ];
    case 'failed':
      return [{ status: 'scheduled', label: 'Назначить повторно', variant: 'secondary' }];
    default:
      return [];
  }
}

/** Акт АОСР заявки: не собирался | живой | удалён (в корзине или вычищен) |
 *  состояние не известно (сервер признака не прислал, проверить не удалось). */
export type InspectionActState = 'none' | 'live' | 'deleted' | 'unknown';

/**
 * Состояние акта заявки. Признак удаления — с сервера (`act_deleted`),
 * без него — проверка карточки акта в ОСНОВА.Инженер (`probe.deleted`: акт помечен
 * на удаление или не найден). Ссылки на акт нет, а статус «акт собран» остался —
 * акт вычищен из корзины (связь снимается `ON DELETE SET NULL`).
 */
export function inspectionActState(
  r: Pick<FieldInspectionRequest, 'status' | 'executive_act_id' | 'act_deleted'>,
  probe?: { deleted: boolean } | null,
): InspectionActState {
  if (!r.executive_act_id) return r.status === 'act_generated' ? 'deleted' : 'none';
  if (typeof r.act_deleted === 'boolean') {
    return r.act_deleted ? 'deleted' : 'live';
  }
  if (probe) return probe.deleted ? 'deleted' : 'live';
  return 'unknown';
}

/**
 * Собрать АОСР можно по пройденному освидетельствованию (`passed`), пересобрать —
 * если акт заявки удалён (`act_generated` + акт в корзине или вычищен; сервер
 * тогда собирает новый). Отрицательное решение («отклонено», «спорно») сборку
 * запрещает (сервер — 409); решение «принято» без пройденного освидетельствования
 * её не открывает. Живой акт (или акт, состояние которого не известно) — второй
 * не собираем.
 */
export function canGenerateAct(
  r: Pick<FieldInspectionRequest, 'status' | 'executive_act_id' | 'decision'> &
    Partial<Pick<FieldInspectionRequest, 'act_deleted'>>,
  actState: InspectionActState = inspectionActState(r),
): boolean {
  if (r.decision === 'rejected' || r.decision === 'disputed') return false;
  if (actState === 'live' || actState === 'unknown') return false;
  if (r.status === 'passed') return true;
  return r.status === 'act_generated' && actState === 'deleted';
}

/** Решения по заявке (П2) в порядке показа кнопок. */
export const INSPECTION_DECISIONS: readonly string[] = ['accepted', 'rejected', 'disputed'];

/**
 * Решения, которые проверяющий может поставить сейчас (П2), — в том числе
 * СМЕНИТЬ уже поставленное, пока акт не собран: раньше после «отклонено» и
 * повторного освидетельствования заявка оставалась в тупике (ни смены решения,
 * ни сборки акта). Решение — по итогам освидетельствования: «пройдено» — любое
 * из трёх; «есть замечания» — только «отклонено» / «спорно» («принято» без
 * пройденного освидетельствования акт не открывает — нужен повторный осмотр).
 * Акт собран и жив — решение уже не меняем; акт удалён — решение предлагаем
 * сменить, только если оно отрицательное и мешает пересборке. Текущее решение в
 * список не входит.
 */
export function inspectionDecisionOptions(
  r: Pick<FieldInspectionRequest, 'status' | 'executive_act_id' | 'decision'> &
    Partial<Pick<FieldInspectionRequest, 'act_deleted'>>,
  actState: InspectionActState = inspectionActState(r),
): string[] {
  if (actState === 'live' || actState === 'unknown') return [];
  const negative = r.decision === 'rejected' || r.decision === 'disputed';
  let allowed: readonly string[] = [];
  if (r.status === 'passed') {
    allowed = INSPECTION_DECISIONS;
  } else if (r.status === 'act_generated' && actState === 'deleted' && negative) {
    allowed = INSPECTION_DECISIONS;
  } else if (r.status === 'failed') {
    allowed = ['rejected', 'disputed'];
  }
  return allowed.filter((d) => d !== r.decision);
}

/** accepted | rejected | disputed → человекочитаемо. */
export function inspectionDecisionLabel(decision: string): string {
  switch (decision) {
    case 'accepted':
      return 'принято';
    case 'rejected':
      return 'отклонено';
    case 'disputed':
      return 'спорно';
    default:
      return decision;
  }
}

/** Позиция объёма РД проекта (`GET /sites/{id}/rd-positions`) — для перепривязки элемента. */
export interface FieldRdPosition {
  id: string;
  revision_id: string;
  /** rd (текстовое извлечение) | rd_table (таблица спецификации). */
  source_role: string;
  code: string | null;
  designation: string | null;
  name: string;
  unit: string | null;
  quantity: number | null;
  section: string | null;
  source_ref: string;
  page_number: number;
  /** Извлечение не уверено — сверить позицию с листом. Поле может не прийти. */
  needs_review?: boolean;
  /** Решение инженера по позиции: accepted | rejected | disputed; отбракованные
   *  (`rejected`) сервер в список не отдаёт. Поле может не прийти. */
  decision?: string | null;
  /** Пометка сваи из графы «Примечание» («рядовые» / «испытуемые»). Поле может не прийти. */
  qualifier?: string | null;
  /** Модель при последнем «Извлечь объёмы» эту позицию не назвала — оставлена
   *  из-за привязок (элементы, факты, ОЖР) и ждёт проверки. Поле может не прийти. */
  missing_in_extraction?: boolean;
  /** Номер строки в ведомости объёмов ПТО («СП01 №5», «ИИ №3»); null — не
   *  пронумерована. Поле может не прийти. */
  row_label?: string | null;
  /** Подпись части РД («СП01 · Спецификация оборудования, изделий и материалов»).
   *  Поле может не прийти. */
  part_label?: string | null;
}

/** Вариант выбора позиции РД для элемента: представитель склеенных дублей. */
export interface RdPositionChoice {
  /** Представитель группы (его id уходит в привязку). */
  position: FieldRdPosition;
  /** Сколько позиций РД склеено в вариант (текст + таблицы с тем же объёмом). */
  merged: number;
  /** Извлечение представителя помечено «нужна проверка» (как у элемента из РД). */
  needsReview: boolean;
  /** Пометка сваи группы, если известна. */
  qualifier: string | null;
  /** Представителя нет в последнем извлечении РД (оставлен из-за привязок). */
  missingInExtraction: boolean;
  /** Решение инженера по представителю (accepted | disputed), если есть. */
  decision: string | null;
}

/** Латинские двойники кириллицы в шифрах (OCR и CAD пишут по-разному). */
const LATIN_TWINS: Readonly<Record<string, string>> = {
  a: 'а',
  b: 'в',
  c: 'с',
  e: 'е',
  h: 'н',
  k: 'к',
  m: 'м',
  o: 'о',
  p: 'р',
  t: 'т',
  x: 'х',
  y: 'у',
};

/** Ключ наименования для склейки дублей — зеркало `fold_text` + `name_key`
 *  бэкенда: нижний регистр, ё→е, й→и, латинские двойники → кириллица, пробелы схлопнуты. */
function rdNameKey(s: string): string {
  return [...s.toLowerCase()]
    .map((c) => (c === 'ё' ? 'е' : c === 'й' ? 'и' : (LATIN_TWINS[c] ?? c)))
    .join('')
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');
}

/** Заголовок/итог спецификации, а не позиция (зеркало `is_heading_name`). */
function isRdHeading(name: string): boolean {
  const n = rdNameKey(name);
  return !/[\p{L}\p{N}]/u.test(n) || n.startsWith('итого') || n.startsWith('всего');
}

/** Одинаковое количество позиции (зеркало `same_qty`: до 1e-9 относительно). */
function rdSameQty(a: number | null, b: number | null): boolean {
  if (a === null || b === null) return a === b;
  return Math.abs(a - b) <= 1e-9 * Math.max(Math.abs(a), Math.abs(b), 1);
}

/** Единицы совместимы: совпадают или у одной из позиций единицы нет. */
function rdUnitCompatible(a: string | null, b: string | null): boolean {
  const x = a ? rdNameKey(a) : '';
  const y = b ? rdNameKey(b) : '';
  return !x || !y || x === y;
}

/**
 * Позиции РД для выбора позиции элемента — как их отбирает справочник
 * конструктива из РД (`plan_rd_elements` бэкенда, режим «все позиции»): только с
 * количеством больше нуля, без заголовков/итогов и отбракованных инженером.
 * Дубли одной позиции (текстовое и табличное извлечение, повтор таблицы на листе)
 * склеиваются в пределах ревизии: то же наименование и количество, совместимые
 * единица и пометка сваи. Представитель — позиция с единицей, из последнего
 * извлечения (не оставленная из-за привязок) и из таблицы (у неё есть графы
 * «Поз.»/«Примечание»). Порядок — как в РД (по первой позиции группы). Раньше
 * выбор предлагал заголовок «без количества» и тот же объём трижды, в том числе
 * текстовый дубль без единицы, — сверка фактов элемента уходила в «единица не
 * совпадает» или «в РД нет количества». Сервер заголовки и отбракованные уже
 * не отдаёт — отбор здесь страхует старый сервер.
 */
export function rdPositionChoices(positions: readonly FieldRdPosition[]): RdPositionChoice[] {
  const order = new Map(positions.map((p, i) => [p.id, i]));
  const rank = (p: FieldRdPosition): [number, number, number] => [
    p.unit && p.unit.trim() ? 0 : 1,
    p.missing_in_extraction === true ? 1 : 0,
    p.source_role === 'rd_table' ? 0 : 1,
  ];
  // Строка-заголовок таблицы спецификации: табличная, без количества и единицы
  // («Элементы свайного поля»). Позиция без количества из текста листа
  // («Ростверк монолитный») — вариант выбора: объём в РД просто не указан.
  const tableTitle = (p: FieldRdPosition) =>
    p.source_role === 'rd_table' && !p.quantity && !(p.unit && p.unit.trim());
  const cands = positions
    .filter(
      (p) =>
        (p.quantity === null || p.quantity > 0) &&
        !tableTitle(p) &&
        !isRdHeading(p.name) &&
        p.decision !== 'rejected',
    )
    .sort((a, b) => {
      const [ua, ma, ta] = rank(a);
      const [ub, mb, tb] = rank(b);
      return (
        ua - ub ||
        ma - mb ||
        ta - tb ||
        a.page_number - b.page_number ||
        a.source_ref.localeCompare(b.source_ref) ||
        a.id.localeCompare(b.id)
      );
    });

  interface Group {
    rep: FieldRdPosition;
    key: string;
    members: FieldRdPosition[];
    qualifier: string | null;
  }
  const groups: Group[] = [];
  for (const p of cands) {
    const key = rdNameKey(p.name);
    const q = p.qualifier?.trim() || null;
    const g = groups.find(
      (x) =>
        x.rep.revision_id === p.revision_id &&
        x.key === key &&
        rdSameQty(x.rep.quantity, p.quantity) &&
        rdUnitCompatible(x.rep.unit, p.unit) &&
        (!x.qualifier || !q || x.qualifier === q),
    );
    if (g) {
      g.members.push(p);
      g.qualifier = g.qualifier ?? q;
    } else {
      groups.push({ rep: p, key, members: [p], qualifier: q });
    }
  }
  const first = (g: Group) => Math.min(...g.members.map((m) => order.get(m.id) ?? 0));
  return groups
    .sort((a, b) => first(a) - first(b))
    .map((g) => ({
      position: g.rep,
      merged: g.members.length,
      needsReview: g.rep.needs_review === true,
      qualifier: g.qualifier,
      missingInExtraction: g.rep.missing_in_extraction === true,
      decision: g.rep.decision ?? null,
    }));
}

/**
 * Подпись варианта позиции РД (она же — в «чипе» выбранного): наименование с
 * маркой и пометкой сваи, количество с единицей. У рядовых и испытуемых свай
 * одной марки наименования совпадают — различают пометка и количество.
 */
export function rdPositionChoiceLabel(c: RdPositionChoice): string {
  const p = c.position;
  const unit = p.unit?.trim();
  const qty =
    p.quantity === null
      ? 'количество в РД не указано'
      : `${p.quantity.toLocaleString('ru-RU')} ${unit || '(единица не указана)'}`;
  const mark = p.designation || p.code;
  const name = mark && !p.name.includes(mark) ? `${p.name} (${mark})` : p.name;
  return `${c.qualifier ? `${name} — ${c.qualifier}` : name} · ${qty}`;
}

/**
 * Пометки варианта позиции РД в выборе (вторая строка): где в РД, сколько
 * дублей склеено, нет ли позиции в последнем извлечении, уверено ли извлечение
 * и что решил по ней инженер. Решение «принята» снимает «извлечение не
 * уверено»: позицию уже сверили с листом.
 */
export function rdPositionChoiceNotes(c: RdPositionChoice): string[] {
  const out: string[] = [];
  if (c.position.source_ref) out.push(c.position.source_ref);
  if (c.merged > 1) out.push(`повторяется в РД: ${c.merged} (текст и таблицы)`);
  if (c.position.quantity === null) {
    out.push('объём в РД не указан — сверить факты элемента с РД будет нельзя');
  }
  if (c.missingInExtraction) {
    out.push('нет в последнем извлечении РД — оставлена из-за привязок, сверьте с листом');
  }
  if (c.decision === 'disputed') out.push('спорная (разметка ПТО)');
  if (c.decision === 'accepted') out.push('проверена ПТО');
  else if (c.needsReview && !c.missingInExtraction) {
    out.push('извлечение не уверено — сверьте с листом');
  }
  return out;
}

/** Форма слова по числу: «1 факт», «2 факта», «5 фактов» (формы — для 1, 2–4, 5+). */
function ruPlural(n: number, forms: readonly [string, string, string]): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}

/** Итог перепривязки элемента к позиции РД словами: что сделано и о чём предупредить. */
export interface ElementPositionResult {
  text: string;
  /** Принятые факты остались на прежней позиции; null — предупреждать не о чем. */
  warning: string | null;
}

/**
 * Ответ `POST /elements/{id}/position` словами. `relinked_facts` — все
 * перенесённые факты элемента: без позиции и нерешённые с прежней позиции.
 * `kept_accepted_facts` — принятые факты элемента на других позициях РД: их
 * сервер не трогает (решения ПТО и строки ОЖР остаются), об этом предупреждаем.
 * Поля `kept_accepted_facts` у старого сервера нет — предупреждения нет.
 */
export function elementPositionResultText(res: {
  relinked_facts?: number | null;
  kept_accepted_facts?: number | null;
}): ElementPositionResult {
  const moved = res.relinked_facts ?? 0;
  const kept = res.kept_accepted_facts ?? 0;
  return {
    text:
      moved > 0
        ? `Привязан к позиции РД. Перенесено фактов: ${moved} — сверка пересчитана.`
        : 'Привязан к позиции РД.',
    warning:
      kept > 0
        ? `${kept} ${ruPlural(kept, [
            'принятый факт остался',
            'принятых факта остались',
            'принятых фактов остались',
          ])} на прежней позиции РД (решения ПТО и строки ОЖР не переносятся).`
        : null,
  };
}

/* ── Уведомления ленты платформы о рапортах и фактах Прораба ────────────────── */

/** Куда ведёт уведомление: путь страницы веба и подсказка к переходу. */
export interface FieldNotificationLink {
  to: string;
  title: string;
}

const UUID_PART = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const REF_REPORT = new RegExp(`^(${UUID_PART})/reports/(${UUID_PART})$`, 'i');
const REF_FACT = new RegExp(`^(${UUID_PART})/(?:volume-facts|facts)/(${UUID_PART})$`, 'i');
const REF_BARE = new RegExp(`^${UUID_PART}$`, 'i');

/**
 * Страница веба для уведомления о рапорте или факте объёма Прораба (лента
 * платформы, колокольчик). Рапорт: `ref_type` `field_daily_report` (или
 * `daily_report`), `ref_id` — путь «{площадка}/reports/{рапорт}» (так пишет
 * «Рапорт дополнен после сдачи») или голый id рапорта. Факт: `field_volume_fact`
 * (или `volume_fact`, `field_fact`), `ref_id` — «{площадка}/volume-facts/{факт}»,
 * «{площадка}/facts/{факт}» или голый id факта («Прораб не согласен с правкой
 * объёма»). Голый id открывается страницей без площадки в пути — площадку она
 * берёт из рапорта/факта. Другое — null (не наше уведомление или битая ссылка).
 */
export function fieldNotificationLink(
  refType: string | null | undefined,
  refId: string | null | undefined,
): FieldNotificationLink | null {
  const id = refId?.trim();
  if (!refType || !id) return null;
  switch (refType) {
    case 'field_daily_report':
    case 'daily_report': {
      const m = REF_REPORT.exec(id);
      if (m) return { to: `/agents/field/${m[1]}/reports/${m[2]}`, title: 'Открыть рапорт' };
      return REF_BARE.test(id)
        ? { to: `/agents/field/reports/${id}`, title: 'Открыть рапорт' }
        : null;
    }
    case 'field_volume_fact':
    case 'volume_fact':
    case 'field_fact': {
      const m = REF_FACT.exec(id);
      if (m) {
        return { to: `/agents/field/${m[1]}/volume-facts/${m[2]}`, title: 'Открыть факт объёма' };
      }
      return REF_BARE.test(id)
        ? { to: `/agents/field/volume-facts/${id}`, title: 'Открыть факт объёма' }
        : null;
    }
    case 'inspection_request':
    case 'field_inspection_request':
      return { to: `/agents/field?inspection=${id}`, title: 'Открыть заявку на освидетельствование' };
    case 'task':
    case 'field_task':
      return { to: `/agents/field?task=${id}`, title: 'Открыть задачу площадки' };
    case 'material_receipt':
    case 'field_material_receipt':
      return { to: `/agents/field?receipt=${id}`, title: 'Открыть приёмку ТТН' };
    case 'device':
    case 'field_device':
      return { to: `/agents/field?device=${id}`, title: 'Открыть устройство' };
    default:
      return null;
  }
}

/** Приёмка материала (ТТН и паспорт качества) на площадке (0135). */
export interface FieldMaterialReceipt {
  id: string;
  site_id: string;
  report_id: string | null;
  element_id: string | null;
  inspection_request_id: string | null;
  passport_id: string | null;
  lot_id: string | null;
  ttn_no: string | null;
  ttn_date: string | null;
  supplier: string | null;
  nomenclature: string | null;
  qty_delivered: string | null;
  unit: string | null;
  qty_used: string | null;
  application_date: string | null;
  source_doc: Record<string, unknown> | null;
  doc_content_type: string | null;
  raw_ocr_text: string | null;
  confidence: number | null;
  needs_review: boolean;
  provenance: string | null;
  status: string;
  note: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

